import type { JSONContent } from "@tiptap/core";
import type { Editor } from "@tiptap/vue-3";
import type { EventHook } from "@vueuse/core";
import type { Raw } from "vue";
import type { Rect } from "../interop/models";
import type { AxFullTextStyle } from "../platform/attributedString";
import { createEventHook } from "@vueuse/core";
import { defineStore } from "pinia";
import { computed, markRaw, ref } from "vue";
import { AxStringAlignment } from "../platform/attributedString";
import { toWidgetStyleFromEditorState } from "../platform/textEditorStyles";

export interface AxTextEditorState {
  id: number;
  syncId?: string;
  typeName?: string;
  rect: Rect;
  visible: boolean;
  content?: JSONContent;
  verticalAlign: AxStringAlignment;
  editor: Raw<Editor> | undefined;
  baseStyle?: AxFullTextStyle;
  allowMentions?: boolean;
  fitToWidth?: boolean;

  meta?: Record<string, string>;

  setContent: (c: JSONContent) => void;
  setEditor: (e: Editor) => void;
  // setVerticalAlignment: (val: AxStringAlignment) => void;

  afterSaved: EventHook<string>;

  noScale?: boolean;
}

export interface WebRichTextObjectEditorInfo {
  typeName?: string;
  syncId?: string;
  meta?: Record<string, string>;
}

export const useDiagramTextEditorsStore = defineStore("textEditors", () => {
  const editors = ref(new Map<number, AxTextEditorState>());

  function create(id: number, editorInfo: WebRichTextObjectEditorInfo) {
    const syncId = editorInfo.syncId;
    if (syncId !== undefined && syncId !== null && (typeof syncId !== "string" || !syncId)) {
      throw new Error(`Failed to create text editor. Expected string syncId but received ${syncId}.`);
    }

    editors.value.set(id, {
      id,
      syncId,
      rect: {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      },
      visible: false,
      verticalAlign: AxStringAlignment.Center,
      editor: undefined,
      typeName: editorInfo.typeName,
      meta: editorInfo.meta,
      allowMentions: editorInfo.typeName === "Axure:DiagramObject:Card",
      setContent(val) {
        this.content = val;
      },
      setEditor(val) {
        this.editor = markRaw(val);
      },
      afterSaved: createEventHook<string>(),
      noScale: editorInfo.typeName === "Axure:DiagramObject:Layer",
    });
  };

  function destroy(id: number) {
    editors.value.delete(id);
  }

  const activeEditorState = computed(() => {
    for (const ed of editors.value.values()) {
      if (ed.visible) {
        return ed;
      }
    }
    return undefined;
  });

  const activeEditor = computed(() => {
    const state = activeEditorState.value;
    return state?.editor;
  });

  const hasActiveEditor = computed(() => {
    return activeEditorState.value !== undefined;
  });

  const activeEditorStyles = computed(() => {
    const editorState = activeEditorState.value;
    if (!editorState) return undefined;
    const styles = toWidgetStyleFromEditorState(editorState);
    return styles;
  });

  return {
    editors,
    create,
    destroy,
    activeEditor,
    activeEditorState,
    activeEditorStyles,
    hasActiveEditor,
  };
});
